/**
 * Logout template.
 */
import Body from '@components/sections/logout/logoutBody';
import Button from '@components/elements/logout/logoutButton';
import CmpUtils from '@shared/utils/cmp';
import Container from '@components/atoms/container/logoutContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import MenuContext from '@shared/components/utils/context/menuContext';
import Modals from '@shared/types/enums/modals';
import Viewport from '@components/atoms/container/contentContainer';
import initAnonymous from '@shared/actions/login/initAnonymous';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { popRoute } from '@shared/store/slices/router.slice';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Logout template component
 * Handles the logout functionality and display of the logout modal.
 *
 * @returns {JSX.Element} Logout container with logout information and button
 */
export default function LogoutTemplate() {
  // Hooks
  const { callbacks, props } = useLogoutTemplate();

  // Render
  return (
    <Viewport id={'logoutContainer'}>
      <Container {...props.container} {...callbacks.container}>
        <Body />
        <Button {...props.button} {...callbacks.button} />
      </Container>
    </Viewport>
  );
}

/**
 * Hook to use the logout functionality and display of the logout modal.
 *
 * @returns {Object} Object with the logout functionality and display of the logout modal
 */
function useLogoutTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch

  const dispatch = useAppDispatch();

  // Context

  const { setListIndex } = useContext(MenuContext);

  // Selectors

  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);

  // State
  const [displaySpinner, setDisplaySpinner] = useState(false);

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const logout = useCallback(() => {
    dispatch(
      setModalParams({
        callback: () => {
          if (FlavourConfig.capabilities.cmp) {
            setDisplaySpinner(true);
            CmpUtils.openBanner(() => {
              initAnonymous(true);
              setDisplaySpinner(false);
            });
          } else {
            initAnonymous(true);
          }
          setListIndex?.current(0);
        },
      })
    );
    dispatch(setModal(Modals.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.LOGOUT));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Logout keyboard
  useKeyboard({
    focus: Focus.LOGOUT,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'SELECT':
          logout();
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.LOGOUT));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const buttonFocused = focus === Focus.LOGOUT;
    const menuFocused = focus === Focus.MENU;
    return {
      callbacks: {
        button: {
          callback: logout,
        },
        container: {
          onMouseEnter,
        },
      },
      props: {
        button: {
          displaySpinner,
          focused: !enabled && buttonFocused,
        },
        container: {
          animation: menuFocused ? 'main-content-out' : 'main-content-in',
          force: true,
        },
      },
    };
  }, [enabled, focus, onMouseEnter]);
}
